import classNames from 'classnames';
import FormFieldLabel from './FormFieldLabel';

export type ButtonToggleProps<T> = {
  options: { value: T; label: string; disabled?: boolean }[];
  label?: string;
  labelTooltip?: string;
  onTooltipHover?: () => void;
  value: T;
  name?: string;
  disabled?: boolean;
  onChange?: (value: T) => void;
  containerClassNames?: string;
};

export const ButtonToggle = <T extends string | number>(
  props: ButtonToggleProps<T>,
) => {
  const {
    options,
    label,
    labelTooltip,
    onTooltipHover,
    value,
    disabled: componentDisabled = false,
    onChange = dumpOnChange,
    containerClassNames,
  } = props;

  return (
    <div
      className={classNames(
        containerClassNames,
        'flex flex-col gap-spacing/gap/soft-6px',
      )}
    >
      {!!label && (
        <FormFieldLabel
          label={label}
          tooltip={labelTooltip}
          onTooltipHover={onTooltipHover}
        />
      )}
      <input
        disabled={componentDisabled}
        type="text"
        hidden
        required
        name={props.name}
        value={value}
        onChange={(event) => {
          if (
            !event.currentTarget.value ||
            options.find((option) => option.value === event.currentTarget.value)
              ?.disabled
          ) {
            return;
          }

          onChange(event.currentTarget.value as T);
        }}
      />
      <div
        role="tablist"
        tabIndex={-1}
        className={classNames(
          'flex h-button/size w-full items-center overflow-hidden rounded-radius/button p-button/padding-toggle transition-all duration-200 bg-fill/alternative-2',
        )}
      >
        {options.map((option) => {
          const selected = option.value === value;
          const disabled = componentDisabled || !!option.disabled;

          return (
            <div
              key={option.value}
              className={classNames('flex h-full flex-1 grow items-center')}
            >
              <div
                data-testid={props.name}
                role="button"
                aria-disabled={disabled || undefined}
                className={classNames(
                  'flex size-full select-none items-center justify-center text-nowrap rounded-radius/button text-center transition-all duration-200 alpha-form-input lg:px-button/padding-regular-x',
                  selected && 'shadow-2 bg-white',
                  selected && !disabled && 'text-text/primary',
                  selected && disabled && 'text-buttons/disable',
                  !selected &&
                    !disabled &&
                    'text-buttons/neutral hover:text-text/primary',
                  !selected && disabled && 'text-buttons/disable',
                  disabled && 'cursor-not-allowed',
                )}
                {...(!disabled && {
                  onClick: () => {
                    if (value === option.value) {
                      return;
                    }
                    onChange(option.value);
                  },
                  tabIndex: 0,
                })}
              >
                {option.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const dumpOnChange = () => {};
